import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { toast } from "sonner";
import { Eye, EyeOff, AlertTriangle, Server } from "lucide-react";
import { Message } from "@/types/chat";
import { callTeamGptApi } from "@/services/teamGptService";
import MessageList from "@/components/chat/MessageList";
import ChatInput from "@/components/chat/ChatInput";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const GibletChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [hideThread, setHideThread] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isApiKeyDialogOpen, setIsApiKeyDialogOpen] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [tempApiKey, setTempApiKey] = useState("");
  const [tempOrganizationId, setTempOrganizationId] = useState("");
  const location = useLocation();
  const isGibletPage = location.pathname === "/giblet";

  const handleSend = async (userMessage: string) => {
    setMessages(prev => [...prev, { text: userMessage, sender: "user" }]);
    setIsLoading(true);
    setShowMessages(true);
    setHideThread(false);
    setApiError(null);
    
    try {
      console.log("Sending message to TeamGPT API:", userMessage);
      
      const isEngagePage = location.pathname === "/engage";
      let contextualizedMessage = userMessage;
      
      if (isEngagePage) {
        contextualizedMessage = `I'm on the iSPAI Engage page looking at opportunities to contribute knowledge and issue calls. Please answer in the context of iSPAI engagement features: ${userMessage}`;
      }
      
      const response = await callTeamGptApi(contextualizedMessage, apiKey, organizationId);
      
      if (response.includes("CORS restrictions") || 
          response.includes("I'm Giblet, your AI assistant")) {
        setApiError("browser_cors");
        
        setMessages(prevMessages => [
          ...prevMessages,
          { text: response, sender: "giblet" }
        ]);
      }
      else if (response.startsWith("Sorry, the TeamGPT API request was not successful")) {
        console.error("API error response:", response);
        setApiError("api_error");
        
        toast.error("API request failed. See chat for details.", {
          description: "Check console logs for more information."
        });
        
        setMessages(prevMessages => [
          ...prevMessages,
          { 
            text: response, 
            sender: "giblet" 
          }
        ]);
      } else {
        setMessages(prevMessages => [
          ...prevMessages,
          { text: response, sender: "giblet" }
        ]);
      }
    } catch (error) {
      console.error("Error processing request:", error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      toast.error("Couldn't process your request", {
        description: errorMessage
      });
      
      setMessages((prevMessages) => [
        ...prevMessages,
        { 
          text: `I encountered an unexpected error: ${errorMessage}. Please try again later.`, 
          sender: "giblet" 
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputFocus = () => {
    if (messages.length > 0) {
      setShowMessages(true);
    }
  };

  const handleClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.messages-container') && !target.closest('.search-input')) {
      setShowMessages(false);
    }
  };

  const toggleThreadVisibility = () => {
    setHideThread(!hideThread);
    if (hideThread && messages.length > 0) {
      setShowMessages(true);
    }
  };

  const openApiKeyDialog = () => {
    setTempApiKey(apiKey);
    setTempOrganizationId(organizationId);
    setIsApiKeyDialogOpen(true);
  };

  const saveApiKey = () => {
    if (tempApiKey.trim()) {
      setApiKey(tempApiKey.trim());
      localStorage.setItem("teamgpt_api_key", tempApiKey.trim());
      
      // Save organization ID if provided
      setOrganizationId(tempOrganizationId.trim());
      if (tempOrganizationId.trim()) {
        localStorage.setItem("teamgpt_org_id", tempOrganizationId.trim());
      } else {
        localStorage.removeItem("teamgpt_org_id");
      }
      
      toast.success("API credentials updated successfully");
    }
    setIsApiKeyDialogOpen(false);
  };

  useState(() => {
    const savedApiKey = localStorage.getItem("teamgpt_api_key");
    if (savedApiKey) {
      setApiKey(savedApiKey);
    }
    
    const savedOrgId = localStorage.getItem("teamgpt_org_id");
    if (savedOrgId) {
      setOrganizationId(savedOrgId);
    }
  });

  return (
    <div className="flex flex-col relative" onClick={handleClickOutside}>
      <Toaster position="top-center" />
      
      {apiError === "api_error" && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start">
          <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">TeamGPT API connection issue</p>
            <p className="text-xs text-amber-700 mt-1">
              We're unable to connect to the TeamGPT API. This could be due to:
            </p>
            <ul className="text-xs text-amber-700 list-disc pl-5 mt-1">
              <li>Network or CORS restrictions</li>
              <li>Invalid API key or permissions</li>
              <li>API endpoint unavailability</li>
              <li>Missing organization ID if required</li>
            </ul>
            <p className="text-xs text-amber-700 mt-1">
              See console logs for detailed information.
            </p>
          </div>
        </div>
      )}
      
      {apiError === "browser_cors" && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start space-x-2">
          <Server className="h-5 w-5 text-blue-500 mr-1 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-800">Server-side proxy needed</p>
            <p className="text-xs text-blue-700 mt-1">
              Browser security (CORS) is preventing direct API calls to TeamGPT. To fix this:
            </p>
            <ol className="text-xs text-blue-700 list-decimal pl-5 mt-1 space-y-1">
              <li>Create a server-side proxy (Node.js, Python, etc.) that forwards requests to TeamGPT</li>
              <li>Deploy this proxy on the same domain as your app or with proper CORS headers</li>
              <li>Update this app to use your proxy endpoint instead of calling TeamGPT directly</li>
            </ol>
            <p className="text-xs text-blue-700 mt-1">
              A serverless function (AWS Lambda, Netlify Functions, etc.) is an ideal solution for this.
            </p>
          </div>
        </div>
      )}
      
      <div className="flex items-center space-x-2">
        <ChatInput 
          isLoading={isLoading}
          isGibletPage={isGibletPage}
          onSendMessage={handleSend}
          onInputFocus={handleInputFocus}
        />
      </div>

      {messages.length > 0 && (
        <div className="absolute top-12 right-full mr-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={toggleThreadVisibility}
            className="p-1 h-8 w-8"
            title={hideThread ? "Show chat thread" : "Hide chat thread"}
          >
            {hideThread ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </Button>
        </div>
      )}

      <MessageList 
        messages={messages} 
        showMessages={showMessages && !hideThread} 
      />

      <Dialog open={isApiKeyDialogOpen} onOpenChange={setIsApiKeyDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Giblet's API Credentials</DialogTitle>
            <DialogDescription>
              Enter your TeamGPT API key and organization ID to connect to the AI service.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="api-key">API Key</Label>
              <Input
                id="api-key"
                type="password"
                placeholder="sk-proj-xxxxxxxx"
                value={tempApiKey}
                onChange={(e) => setTempApiKey(e.target.value)}
                className="w-full"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="org-id" className="flex items-center">
                <span>Organization ID</span>
                <span className="text-xs text-muted-foreground ml-2">(Optional)</span>
              </Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="org-id"
                  type="text"
                  placeholder="org-xxxxxxxx"
                  value={tempOrganizationId}
                  onChange={(e) => setTempOrganizationId(e.target.value)}
                  className="w-full"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                If your API requests require an organization ID, enter it here.
              </p>
            </div>
            
            <p className="text-xs text-muted-foreground">
              Your credentials are stored locally in your browser and never sent to our servers.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsApiKeyDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveApiKey}>Save Credentials</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GibletChat;
